import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useNotification } from '../../contexts/NotificationContext'
import api from '../../services/api'
import {
  ArrowLeft,
  Loader2,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  ExternalLink,
  Star,
  Eye,
  MousePointer,
  MessageSquare,
  TrendingUp,
  Send,
  Trash2,
  Pause,
  Play,
  Calendar,
  BarChart3,
} from 'lucide-react'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'
import PortalLogo from '../../components/integracoes/PortalLogo'
import { StatCard, Panel, Card, Badge, Button, buttonClass } from '../../design-system'

interface PublicacaoDetalhes {
  id: number
  veiculoId: number
  portal: string
  status: string
  idExterno: string | null
  urlAnuncio: string | null
  precoWeb: number | null
  destaque: boolean
  tipoDestaque: string | null
  penalizado: boolean
  motivoPenalizacao: string | null
  visualizacoes: number
  cliques: number
  mensagens: number
  ligacoes: number
  dataPublicacao: string | null
  dataAtualizacao: string | null
  dataExpiracao: string | null
  ultimaSincronizacao: string | null
  sincronizadoComPlataforma: boolean
  // Veículo
  veiculo: {
    id: number
    descricao: string
    marca: string
    modelo: string
    versao: string
    anoFabricacao: number
    anoModelo: number
    cor: string
    km: number
    placa: string
    status: string
    valorVenda: number
    valorPromocional: number | null
    fotoPrincipal: string | null
    fotos: string[]
  }
  // Histórico de métricas
  historico: {
    data: string
    visualizacoes: number
    cliques: number
    mensagens: number
  }[]
}

export default function PublicacaoDetalhe() {
  const { veiculoId, portal } = useParams<{ veiculoId: string; portal: string }>()
  const navigate = useNavigate()
  const { showApiError, showSuccess } = useNotification()
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState<PublicacaoDetalhes | null>(null)
  const [actionLoading, setActionLoading] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    fetchDetalhes(controller.signal)
    return () => controller.abort()
  }, [veiculoId, portal])

  const fetchDetalhes = async (signal?: AbortSignal) => {
    try {
      setLoading(true)
      const response = await api.get(`/portais/publicacoes/${veiculoId}/${portal}`, { signal })
      setData(response.data)
    } catch (error: unknown) {
      if ((error as { name?: string })?.name !== 'CanceledError') {
        showApiError(error)
      }
    } finally {
      setLoading(false)
    }
  }

  const executarAcao = async (acao: string) => {
    try {
      setActionLoading(acao)
      await api.post(`/portais/publicacoes/${veiculoId}/${portal}/${acao}`)
      showSuccess(`Ação "${acao}" executada com sucesso`)
      fetchDetalhes()
    } catch (error) {
      showApiError(error)
    } finally {
      setActionLoading(null)
    }
  }

  const formatCurrency = (value: number | null | undefined) => {
    if (!value) return '-'
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)
  }

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'PUBLICADO':
        return <CheckCircle2 className="w-3.5 h-3.5" />
      case 'ERRO':
        return <XCircle className="w-3.5 h-3.5" />
      case 'AGUARDANDO':
      case 'DESATUALIZADO':
        return <Clock className="w-3.5 h-3.5" />
      case 'PAUSADO':
        return <Pause className="w-3.5 h-3.5" />
      default:
        return <Clock className="w-3.5 h-3.5" />
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'PUBLICADO':
        return 'Publicado'
      case 'ERRO':
        return 'Erro'
      case 'AGUARDANDO':
        return 'Aguardando'
      case 'DESATUALIZADO':
        return 'Desatualizado'
      case 'PAUSADO':
        return 'Pausado'
      case 'REMOVIDO':
        return 'Removido'
      default:
        return 'Não Publicado'
    }
  }

  const getStatusTone = (status: string): 'success' | 'danger' | 'warning' | 'neutral' => {
    switch (status) {
      case 'PUBLICADO':
        return 'success'
      case 'ERRO':
        return 'danger'
      case 'AGUARDANDO':
      case 'DESATUALIZADO':
      case 'PAUSADO':
        return 'warning'
      default:
        return 'neutral'
    }
  }

  const taxaConversao = data && data.visualizacoes > 0
    ? ((data.mensagens / data.visualizacoes) * 100).toFixed(2)
    : '0.00'

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    )
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <p className="text-slate-500 mb-4">Publicação não encontrada</p>
        <Link to="/integracoes/publicacoes" className={buttonClass('primary')}>
          Voltar para Publicações
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-slate-500">
        <Link to="/integracoes" className="hover:text-primary-600">
          Dashboard
        </Link>
        <span>/</span>
        <Link to="/integracoes/publicacoes" className="hover:text-primary-600">
          Publicações
        </Link>
        <span>/</span>
        <span className="text-slate-900">{data?.portal || 'Detalhes'}</span>
      </nav>

      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-slate-100 rounded-xl text-slate-600"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <PortalLogo portal={data.portal} size="md" />
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-slate-900">
                {data.portal}
              </h1>
              <p className="text-slate-500">
                {data.veiculo.descricao}
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge tone={getStatusTone(data.status)}>
            {getStatusIcon(data.status)}
            {getStatusText(data.status)}
          </Badge>
          <Button variant="secondary" onClick={() => fetchDetalhes()}>
            <RefreshCw className="w-4 h-4" />
            Atualizar
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Coluna Principal */}
        <div className="lg:col-span-2 space-y-6">
          {/* Cards de Métricas */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              title="Visualizações"
              value={data.visualizacoes.toLocaleString('pt-BR')}
              icon={<Eye className="h-[18px] w-[18px]" />}
              delay={0}
            />
            <StatCard
              title="Cliques"
              value={data.cliques.toLocaleString('pt-BR')}
              icon={<MousePointer className="h-[18px] w-[18px]" />}
              delay={60}
            />
            <StatCard
              title="Mensagens"
              value={data.mensagens.toLocaleString('pt-BR')}
              icon={<MessageSquare className="h-[18px] w-[18px]" />}
              delay={120}
            />
            <StatCard
              title="Taxa Conv."
              value={`${taxaConversao}%`}
              icon={<TrendingUp className="h-[18px] w-[18px]" />}
              delay={180}
            />
          </div>

          {/* Gráfico de Evolução */}
          {data.historico && data.historico.length > 0 && (
            <Panel title="Evolução de Métricas" icon={<BarChart3 className="h-4 w-4" />}>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={data.historico} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <defs>
                    <linearGradient id="colorVis" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorClick" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#22c55e" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorMsg" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#a855f7" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#a855f7" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="data"
                    tickFormatter={(value) => {
                      const date = new Date(value)
                      return `${date.getDate()}/${date.getMonth() + 1}`
                    }}
                  />
                  <YAxis />
                  <Tooltip
                    labelFormatter={(value) => {
                      const date = new Date(value)
                      return date.toLocaleDateString('pt-BR')
                    }}
                  />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="visualizacoes"
                    stroke="#3b82f6"
                    fillOpacity={1}
                    fill="url(#colorVis)"
                    name="Visualizações"
                  />
                  <Area
                    type="monotone"
                    dataKey="cliques"
                    stroke="#22c55e"
                    fillOpacity={1}
                    fill="url(#colorClick)"
                    name="Cliques"
                  />
                  <Area
                    type="monotone"
                    dataKey="mensagens"
                    stroke="#a855f7"
                    fillOpacity={1}
                    fill="url(#colorMsg)"
                    name="Mensagens"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Panel>
          )}

          {/* Detalhes da Publicação */}
          <Panel title="Detalhes da Publicação">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-slate-500">ID Externo</p>
                <p className="font-semibold text-slate-900">{data.idExterno || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Preço Publicado</p>
                <p className="font-semibold text-slate-900 tabular-nums">{formatCurrency(data.precoWeb)}</p>
                {data.precoWeb && data.veiculo.valorVenda && data.precoWeb !== data.veiculo.valorVenda && (
                  <p className="text-xs text-amber-600 flex items-center gap-1 mt-1">
                    <AlertTriangle className="w-3 h-3" />
                    Diferente do preço interno ({formatCurrency(data.veiculo.valorVenda)})
                  </p>
                )}
              </div>
              <div>
                <p className="text-sm text-slate-500">Tipo de Destaque</p>
                <p className="font-semibold text-slate-900 flex items-center gap-2">
                  {data.destaque && <Star className="w-4 h-4 text-amber-500" />}
                  {data.tipoDestaque || 'Normal'}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Sincronizado</p>
                <p className="font-semibold text-slate-900 flex items-center gap-2">
                  {data.sincronizadoComPlataforma ? (
                    <>
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      Sim
                    </>
                  ) : (
                    <>
                      <XCircle className="w-4 h-4 text-slate-400" />
                      Não
                    </>
                  )}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Data de Publicação</p>
                <p className="font-semibold text-slate-900">{formatDate(data.dataPublicacao)}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Última Atualização</p>
                <p className="font-semibold text-slate-900">{formatDate(data.dataAtualizacao)}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Última Sincronização</p>
                <p className="font-semibold text-slate-900">{formatDate(data.ultimaSincronizacao)}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Data de Expiração</p>
                <p className={`font-semibold ${data.dataExpiracao && new Date(data.dataExpiracao) < new Date() ? 'text-rose-600' : 'text-slate-900'}`}>
                  {formatDate(data.dataExpiracao)}
                </p>
              </div>

              {data.urlAnuncio && (
                <div className="col-span-2">
                  <p className="text-sm text-slate-500 mb-1">Link do Anúncio</p>
                  <a
                    href={data.urlAnuncio}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:text-primary-700 flex items-center gap-2"
                  >
                    <ExternalLink className="w-4 h-4" />
                    {data.urlAnuncio}
                  </a>
                </div>
              )}

              {data.penalizado && (
                <div className="col-span-2 p-4 bg-rose-50 rounded-xl">
                  <div className="flex items-center gap-2 text-rose-800 mb-2">
                    <AlertTriangle className="w-5 h-5" />
                    <span className="font-semibold">Anúncio Penalizado</span>
                  </div>
                  <p className="text-sm text-rose-700">
                    {data.motivoPenalizacao || 'Motivo não especificado. Verifique as políticas do portal.'}
                  </p>
                </div>
              )}
            </div>
          </Panel>
        </div>

        {/* Coluna Lateral */}
        <div className="space-y-6">
          {/* Card do Veículo */}
          <Card className="overflow-hidden">
            {data.veiculo.fotoPrincipal ? (
              <img
                src={data.veiculo.fotoPrincipal}
                alt={data.veiculo.descricao}
                className="w-full h-48 object-cover"
              />
            ) : (
              <div className="w-full h-48 bg-slate-100 flex items-center justify-center">
                <Eye className="w-12 h-12 text-slate-300" />
              </div>
            )}
            <div className="p-4">
              <h3 className="font-bold tracking-tight text-lg text-slate-900">{data.veiculo.descricao}</h3>
              <p className="text-sm text-slate-500 mb-4">
                {data.veiculo.anoFabricacao}/{data.veiculo.anoModelo} | {data.veiculo.km.toLocaleString()} km
              </p>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Placa</span>
                  <span className="font-semibold text-slate-900">{data.veiculo.placa}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Cor</span>
                  <span className="font-semibold text-slate-900">{data.veiculo.cor}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-500">Status</span>
                  <Badge tone={
                    data.veiculo.status === 'DISPONIVEL' ? 'success' :
                    data.veiculo.status === 'RESERVADO' ? 'warning' :
                    'neutral'
                  }>
                    {data.veiculo.status}
                  </Badge>
                </div>
                <div className="flex justify-between pt-2 border-t border-slate-100">
                  <span className="text-slate-500">Preço Interno</span>
                  <span className="font-bold text-primary-600 tabular-nums">
                    {formatCurrency(data.veiculo.valorVenda)}
                  </span>
                </div>
                {data.veiculo.valorPromocional && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Preço Promocional</span>
                    <span className="font-bold text-emerald-600 tabular-nums">
                      {formatCurrency(data.veiculo.valorPromocional)}
                    </span>
                  </div>
                )}
              </div>

              <Link
                to={`/veiculos/${data.veiculo.id}`}
                className={`mt-4 w-full ${buttonClass('secondary', 'sm')}`}
              >
                Ver Veículo Completo
              </Link>
            </div>
          </Card>

          {/* Ações */}
          <Panel title="Ações">
            <div className="space-y-2">
              {data.status !== 'PUBLICADO' && data.status !== 'AGUARDANDO' && (
                <Button
                  onClick={() => executarAcao('publicar')}
                  disabled={actionLoading !== null}
                  className="w-full"
                >
                  {actionLoading === 'publicar' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  Publicar
                </Button>
              )}

              {data.status === 'PUBLICADO' && (
                <>
                  <Button
                    variant="secondary"
                    onClick={() => executarAcao('atualizar')}
                    disabled={actionLoading !== null}
                    className="w-full"
                  >
                    {actionLoading === 'atualizar' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )}
                    Atualizar Anúncio
                  </Button>

                  <Button
                    variant="secondary"
                    onClick={() => executarAcao('pausar')}
                    disabled={actionLoading !== null}
                    className="w-full"
                  >
                    {actionLoading === 'pausar' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Pause className="w-4 h-4" />
                    )}
                    Pausar Anúncio
                  </Button>
                </>
              )}

              {data.status === 'PAUSADO' && (
                <Button
                  onClick={() => executarAcao('reativar')}
                  disabled={actionLoading !== null}
                  className="w-full"
                >
                  {actionLoading === 'reativar' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Play className="w-4 h-4" />
                  )}
                  Reativar Anúncio
                </Button>
              )}

              {(data.status === 'PUBLICADO' || data.status === 'PAUSADO') && (
                <Button
                  variant="danger"
                  onClick={() => {
                    if (window.confirm('Deseja remover este anúncio do portal?')) {
                      executarAcao('remover')
                    }
                  }}
                  disabled={actionLoading !== null}
                  className="w-full"
                >
                  {actionLoading === 'remover' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                  Remover Anúncio
                </Button>
              )}

              <Button
                variant="secondary"
                onClick={() => executarAcao('sincronizar')}
                disabled={actionLoading !== null}
                className="w-full"
              >
                {actionLoading === 'sincronizar' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                Sincronizar com Portal
              </Button>
            </div>
          </Panel>

          {/* Datas */}
          <Panel title="Linha do Tempo" icon={<Calendar className="h-4 w-4" />}>
            <div className="space-y-3">
              {data.dataPublicacao && (
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-emerald-500 mt-2" />
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Publicado</p>
                    <p className="text-xs text-slate-500">{formatDate(data.dataPublicacao)}</p>
                  </div>
                </div>
              )}
              {data.dataAtualizacao && data.dataAtualizacao !== data.dataPublicacao && (
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-primary-500 mt-2" />
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Última Atualização</p>
                    <p className="text-xs text-slate-500">{formatDate(data.dataAtualizacao)}</p>
                  </div>
                </div>
              )}
              {data.ultimaSincronizacao && (
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-slate-400 mt-2" />
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Última Sincronização</p>
                    <p className="text-xs text-slate-500">{formatDate(data.ultimaSincronizacao)}</p>
                  </div>
                </div>
              )}
              {data.dataExpiracao && (
                <div className="flex items-start gap-3">
                  <div className={`w-2 h-2 rounded-full mt-2 ${
                    new Date(data.dataExpiracao) < new Date() ? 'bg-rose-500' : 'bg-slate-400'
                  }`} />
                  <div>
                    <p className="text-sm font-semibold text-slate-900">
                      {new Date(data.dataExpiracao) < new Date() ? 'Expirado em' : 'Expira em'}
                    </p>
                    <p className="text-xs text-slate-500">{formatDate(data.dataExpiracao)}</p>
                  </div>
                </div>
              )}
            </div>
          </Panel>
        </div>
      </div>
    </div>
  )
}
